import { useQuery } from "@apollo/client/react";
import { HiBookOpen } from "react-icons/hi";
import {
  LessonProgressListDocument,
  LessonRetentionDocument,
  LessonTimeInvestmentDocument,
} from "../../../generated/graphql";
import type { FilterState } from "../../../pages/fsrs/AnalyticsDashboardPage";
import { LessonProgressBars } from "../charts/LessonProgressBars";
import { LessonRetentionTable } from "../charts/LessonRetentionTable";
import { TimeInvestmentChart } from "../charts/TimeInvestmentChart";

interface LessonsTabProps {
  filters: FilterState;
}

export function LessonsTab({ filters }: LessonsTabProps) {
  const { selectedStudentId, selectedProjectId } = filters;

  const { data: retentionData, loading: retentionLoading } = useQuery(LessonRetentionDocument, {
    variables: {
      studentId: selectedStudentId || "",
      projectId: selectedProjectId || null,
      sortBy: "retentionRate",
      sortOrder: "desc",
    },
    skip: !selectedStudentId,
  });

  const { data: progressData, loading: progressLoading } = useQuery(LessonProgressListDocument, {
    variables: {
      studentId: selectedStudentId || "",
      projectId: selectedProjectId || null,
    },
    skip: !selectedStudentId,
  });

  const { data: timeData, loading: timeLoading } = useQuery(LessonTimeInvestmentDocument, {
    variables: {
      studentId: selectedStudentId || "",
      dateRange: null,
    },
    skip: !selectedStudentId,
  });

  // Empty state if no student selected
  if (!selectedStudentId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <HiBookOpen className="text-6xl mb-4 mx-auto text-gray-600" />
          <h3 className="h5 mb-2">Select a Student</h3>
          <p className="text-gray-600">
            Choose a student from the filters above to view lesson analytics
          </p>
        </div>
      </div>
    );
  }

  const isLoading = retentionLoading || progressLoading || timeLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-600">Loading lesson analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="h4">Lesson Analytics</h2>
        <p className="text-gray-600 mt-1">
          Track lesson-level retention, progress, and time investment
        </p>
      </div>

      {/* Section 1: Lesson Retention Table */}
      <section>
        <h3 className="h5 mb-4">Lesson Retention Overview</h3>
        <LessonRetentionTable lessons={retentionData?.lessonRetention || []} />
      </section>

      {/* Section 2: Lesson Progress Bars */}
      <section>
        <h3 className="h5 mb-4">Lesson Progress Breakdown</h3>
        <LessonProgressBars lessons={progressData?.lessonProgressList || []} />
      </section>

      {/* Section 3: Time Investment Chart */}
      <section>
        <h3 className="h5 mb-4">Time Investment by Lesson</h3>
        <div className="panel p-6">
          <TimeInvestmentChart data={timeData?.lessonTimeInvestment || []} />
        </div>
      </section>
    </div>
  );
}
